import { useState } from "react";
import Image from "next/image";
import { Modal } from "@/components/shared/data-display/modal/modal";
import { Progress } from "@/components/shared/data-display/progress/progress";
import { Button } from "@/components/shared/inputs/button/button";
import { getWorkFirstChapter } from "@/services/work.service";
import { logError } from "@/infrastructure/observability/error-logging.service";
import type { WorkFirstChapter } from "@/types/work.types";
import { sanitizeContentHTML } from "@/utility/html.utility";
import styles from "./work-peek-inside.module.scss";

const PEEK_INSIDE_IMAGE_URL =
  "https://static.thriftbooks.com/site_images/d-peak-inside@2x_cc89bc0c.png";

export interface WorkPeekInsideProps {
  idAmazon: number;
  title: string;
  coverUrl: string;
  coverClassName?: string;
}

const sanitizeChapter = (chapter: string): string =>
  sanitizeContentHTML(chapter).replace(/<\/?a(?:\s[^>]*)?>/gi, "");

/**
 * Displays the Legacy Peek Inside affordance and loads its chapter on demand.
 * @param root0 Component props.
 * @param root0.idAmazon Selected edition identifier.
 * @param root0.title Selected edition title.
 * @param root0.coverUrl Selected edition cover image.
 * @param root0.coverClassName Optional class for the responsive cover image.
 * @returns Peek Inside trigger, cover, and modal.
 */
const WorkPeekInside = ({
  idAmazon,
  title,
  coverUrl,
  coverClassName,
}: WorkPeekInsideProps) => {
  const [open, setOpen] = useState(false);
  const [chapter, setChapter] = useState<WorkFirstChapter | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const loadChapter = async () => {
    if (loading || chapter) return;

    setLoading(true);
    setError(false);
    try {
      setChapter(await getWorkFirstChapter(idAmazon));
    } catch (requestError) {
      logError(requestError, { context: "WorkPeekInside:firstChapter" });
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    void loadChapter();
  };

  return (
    <div className={styles.root}>
      <button
        type="button"
        className={styles.peekButton}
        onClick={handleOpen}
        aria-label={`Peek Inside ${title}`}
      >
        <Image
          src={PEEK_INSIDE_IMAGE_URL}
          alt="Peek Inside"
          width={300}
          height={55}
          sizes="(max-width: 599px) 66px, (max-width: 767px) 78px, (max-width: 899px) 90px, (max-width: 1199px) 126px, 162px"
          className={styles.peekImage}
        />
      </button>
      <button
        type="button"
        className={styles.coverButton}
        onClick={handleOpen}
        aria-label={`Open Peek Inside for ${title}`}
      >
        <Image
          src={coverUrl}
          alt={`Cover of ${title}`}
          width={300}
          height={450}
          priority
          sizes="(max-width: 599px) 110px, (max-width: 767px) 130px, (max-width: 899px) 150px, (max-width: 1199px) 210px, 270px"
          className={coverClassName}
        />
      </button>

      <Modal
        open={open}
        onOpenChange={setOpen}
        isDismissible
        isFixedWidth
        title="Peek Inside"
        primaryButtonText="Close"
        primaryButtonAction={() => setOpen(false)}
        containerClassName={styles.modal}
      >
        <div className={styles.content}>
          {loading && (
            <div
              className={styles.status}
              role="status"
              aria-label="Loading Peek Inside"
            >
              <Progress
                progressType="circular"
                variant="indeterminate"
                size={32}
              />
            </div>
          )}
          {error && (
            <div className={styles.status} role="alert">
              <p>Failed to retrieve first chapter.</p>
              <Button
                text="Try again"
                variant="link"
                onClick={() => void loadChapter()}
              />
            </div>
          )}
          {chapter && (
            <>
              <h5 className={styles.bookTitle}>{title}</h5>
              {chapter.fromOtherEdition && (
                <p className={styles.otherEditionNotice}>
                  This book preview is from another edition of this book.
                </p>
              )}
              <div
                className={styles.chapter}
                dangerouslySetInnerHTML={{
                  __html: sanitizeChapter(chapter.firstChapter),
                }}
              />
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default WorkPeekInside;
